import {
    types,
    flow,
    applySnapshot,
    getParent,
    hasParent
} from "mobx-state-tree";

import * as SocialCredentialsAPI from "api/socialcredentials";
import * as ShareAPI from "api/share";
import CONSTANTS from "const/constants";
import Persistence from "pages/common/lib/grabyo-persistence";
import { hasChanged } from "util/mst-utils";
import { DEFAULT_STUDIO_USER_PROFILE_IMAGE_URL } from "const/images";

import BaseBackboneModelMigrationStore from "../backbone-model-template";

const {
    SOCIAL_CREDENTIAL_STATUS,
    DEPRECATED_CREDENTIAL_TYPES,
    DEFAULT_API_PATH
} = CONSTANTS;

const model = {
    socialId: types.optional(types.identifier, ``),
    userId: types.maybeNull(types.string),
    credentialType: types.maybeNull(types.string),
    enabled: types.maybeNull(types.boolean, false),
    tokenStatus: types.maybeNull(types.string, `unknown`),
    status: types.maybeNull(types.string),
    expiryTimestamp: types.maybeNull(types.integer),
    contentIdApiEnabled: types.maybeNull(types.string)
};

const volatile = (self) => ({
    name: ``,
    profileImage: DEFAULT_STUDIO_USER_PROFILE_IMAGE_URL,
    identifierAttribute: `socialId`,
    type: self.credentialType,
    disabledIfFeatureNotSupported: false
});

const views = (self) => ({
    get id() {
        return self[self.identifierAttribute];
    },

    get platformId() {
        return self.socialId.split(`:`)[1];
    },

    shareUrl(shareId) {
        return `/ws/shares/${shareId}/socialmediashare`;
    },

    versionedShareUrl(shareId) {
        return `${DEFAULT_API_PATH}/shares/${shareId}/social-media`;
    },

    isActive() {
        return self.status === SOCIAL_CREDENTIAL_STATUS.ACTIVE;
    },

    isInvalid() {
        return self.status === SOCIAL_CREDENTIAL_STATUS.INVALID;
    },

    isDeprecated() {
        return DEPRECATED_CREDENTIAL_TYPES.includes(self.type);
    },

    getDisplayName() {
        return self.name;
    },

    getTypeForSharing() {
        return self.isFacebookPage() ? `facebookPage` : self.type;
    },

    isMonetizationEnabled() {
        return false;
    },

    getMonetizationShareInfo() {
        return {};
    },

    isFacebookPage() {
        return self.type === `facebook`;
    },

    isTwitterAccount() {
        return self.type === `twitter`;
    },

    isYoutubeChannel() {
        return self.type === `youtube`;
    },

    isTwitchAccount() {
        return self.type === `twitch`;
    },

    isInstagramAccount() {
        return self.type === `instagram`;
    },

    isThePlatformAccount() {
        return self.type === `theplatform`;
    },

    isFtpAccount() {
        return self.type === `ftp`;
    },

    isTikTokAccount() {
        return self.type === `tiktok`;
    },

    isSpredfastAccount() {
        return self.type === `spredfast`;
    },

    isSocialMedia() {
        return false;
    },

    isClipSharingEnabledForType() {
        return [`opta`, `twitch`].includes(self.type);
    },

    isLiveStreamingEnabledForType() {
        return [`facebook`, `youtube`, `twitch`].includes(self.type);
    },

    has360VideoSupport() {
        return self.isFacebookPage() || self.isYoutubeChannel();
    },

    hasGIFSupport() {
        return (
            self.isFacebookPage() ||
            self.isSpredfastAccount() ||
            self.isTwitterAccount() ||
            self.isThePlatformAccount() ||
            self.isInstagramAccount()
        );
    },

    isFeatureSupported(feature) {
        switch (feature) {
            case `gif`:
                return self.hasGIFSupport();
            case `360video`:
                return self.has360VideoSupport();
            default:
                return false;
        }
    },

    onShareSuccessful(key, response) {
        const grab = App?.Collections?.grabs?.getGrabByShareId(
            response.shareId
        );
        if (grab) {
            grab.set(`shareCount`, grab.get(`shareCount`) + 1);
            grab.share.set(key, grab.share.get(key) + 1);
        }
    },

    getClipMaxDuration() {
        return Infinity;
    },

    getPersistedSettings() {
        return Persistence.PermStore.get(`socialCredential:${self.socialId}`);
    },

    loadPersistedSettings() {
        self.update(self.getPersistedSettings());
    }
});

const actions = (self) => {
    return {
        /**
         * @summary Update model with new data (if changed)
         * @param {UserCredentialObject} data
         */
        update(data) {
            if (
                data &&
                Object.hasOwn(data, self.identifierAttribute) &&
                data[self.identifierAttribute] ===
                    self[self.identifierAttribute]
            ) {
                hasChanged(self, data) && applySnapshot(self, data);
            }
        },

        /**
         * @summary Calls a method on the parent node to destroy this child
         */
        destroy() {
            if (hasParent(self, 2)) {
                getParent(self, 2)?.destroyChild(self);
            }
        },

        setDisabledStatus(value) {
            self.disabledIfFeatureNotSupported = value;
            if (value) {
                self.enabled = false;
            }
        },

        setEnabled(enabled) {
            self.enabled = enabled;
        },

        setContentIdApiEnabled(enabled) {
            self.contentIdApiEnabled = enabled;
        }
    };
};

const asyncActions = (self) => {
    return {
        /**
         * @summary Make an api call to DELETE this user credential
         * @async
         * @return {UserCredentialObject | Error}
         */
        remove: flow(function* remove() {
            try {
                const { data } = yield SocialCredentialsAPI.deleteCredential(
                    self.socialId
                );
                self.destroy();
                return data;
            } catch (error) {
                console.error(error);
                return error;
            }
        }),

        /**
         * @summary Make an api call to toggle the status of this user credential
         * @async
         * @return {UserCredentialObject | Error}
         */
        toggleStatus: flow(function* toggleStatus() {
            try {
                const { data } =
                    yield SocialCredentialsAPI.updateCredentialStatus(
                        !self.enabled
                    );
                self.update(data);
                return data;
            } catch (error) {
                console.error(error);
                self.__model.trigger(`fail:toggleStatus`, self.__model);
                return error;
            }
        }),

        share: flow(function* share(shareId, data) {
            if (
                self.isTwitterAccount() ||
                self.isFacebookPage() ||
                self.isTikTokAccount() ||
                self.isInstagramAccount()
            ) {
                try {
                    const { response } = yield ShareAPI.shareToDestination(
                        shareId,
                        {
                            ...data,
                            type: self.getTypeForSharing(),
                            userSocialCredentialId: self.socialId
                        }
                    );
                    self.onShareSuccessful(shareId, response);
                } catch (error) {
                    console.error(error);
                }
            } else {
                try {
                    const { response } =
                        yield ShareAPI.shareToVersionedDestination(shareId, {
                            ...data,
                            userSocialCredentialId: self.socialId
                        });
                    self.onShareSuccessful(shareId, response);
                } catch (error) {
                    console.error(error);
                }
            }
        })
    };
};

const _UserCredentialBaseStore = types
    .model(`UserCredentialBase`, model)
    .volatile(volatile)
    .views(views)
    .actions(actions)
    .actions(asyncActions);

export const UserCredentialBaseStore = BaseBackboneModelMigrationStore(
    _UserCredentialBaseStore
);
